import { useCallback, useContext, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useFormik } from 'formik'

import { selectedFacebookAdAccountSelector } from '../../../../../../modules/selectors/adAccounts'
import { validatedDomainsSelector } from '../../../../../../modules/selectors/ads'
import {
  selectedPartnershipRightsHolderIdSelector,
  selectedPartnershipSelector
} from '../../../../../../modules/selectors/partnerships'
import { createAd } from '../../../../../../modules/actions/ads'

import getConversionDomain from '../helpers/getConversionDomain'
import getConversionDomainValidation from '../helpers/getConversionDomainValidation'
import usePreselectPixel from './usePreselectPixel'
import { usePurifiedFormik } from '../../../../../../hooks/formHooks/usePurifiedFormik'
import useLineItemDetails from './useLineItemDetails'
import useCampaignDetails from './useCampaignDetails'
import { transformValuesToBE } from '../formatters'

import { getInitialValues } from '../fields'
import { FACEBOOK_PLATFORM } from '../../../../../../constants/selectLists/platformList'
import { PIXEL_CHOICE } from '../../../fields'
import { getValidationSchema } from '../validation'
import usePreselectFacebookPageAndInstagramAccount from './usePreselectFacebookPageAndInstagramAccount'

import { AdFacebookCreateContext } from '../../../../formsContexts/AdFacebookCreateFormContextProvider'

export function useCreateFacebookAd({
  isInternalAdCreation,
  selectedPixelPassedFromOutside,
  selectedApplicationId,
  allowSendPixel,
  formatOption
}) {
  const dispatch = useDispatch()

  const { id: selectedAdAccountId } = useSelector(selectedFacebookAdAccountSelector)
  const validatedDomains = useSelector(validatedDomainsSelector)

  const { preselectedValues, lineItem } = useContext(AdFacebookCreateContext)

  const { utm_medium: partnershipUtmMedium } = useSelector(selectedPartnershipSelector)
  const rightsHolderId = useSelector(selectedPartnershipRightsHolderIdSelector)

  const { lineItemId, isAdSetEndDateInThePast, showAsStory, placementPositions } = useLineItemDetails()
  const { campaignId } = useCampaignDetails()

  const onSubmit = useCallback(
    values => {
      const transformedValues = transformValuesToBE({
        values,
        selectedAdAccountId,
        adSetId: lineItemId,
        campaignId,
        validatedDomains,
        partnershipUtmMedium,
        isInternalAdCreation,
        rightsHolderId,
        selectedApplicationId,
        selectedPixelPassedFromOutside,
        allowSendPixel,
        lineItem
      })

      dispatch(createAd(transformedValues, FACEBOOK_PLATFORM))
    },
    [
      dispatch,
      selectedAdAccountId,
      lineItemId,
      campaignId,
      validatedDomains,
      partnershipUtmMedium,
      isInternalAdCreation,
      rightsHolderId,
      selectedApplicationId,
      selectedPixelPassedFromOutside,
      allowSendPixel,
      lineItem
    ]
  )

  // todo saved form data functionality is disabled till post MVP
  // const savedCreationAdForm = getSessionStorage(AD_FACEBOOK_CREATE, true) || {}
  const formik = useFormik({
    // todo saved form data functionality is disabled till post MVP
    // initialValues: savedCreationAdForm.formikValues || initialValues,
    initialValues: getInitialValues({ formatOption, preselectedValues, lineItem, isInternalAdCreation }),
    validationSchema: getValidationSchema(formatOption, lineItem),
    onSubmit
  })
  const purifiedFormik = usePurifiedFormik(formik)
  const { values, setFieldValue } = purifiedFormik

  const selectedPixel = values[PIXEL_CHOICE]

  const conversionDomain = getConversionDomain(values)

  const { isDomainValidating } = getConversionDomainValidation(conversionDomain, validatedDomains)

  usePreselectPixel({ selectedPixel, setFieldValue })
  usePreselectFacebookPageAndInstagramAccount(purifiedFormik)

  return useMemo(
    () => ({
      isAdSetEndDateInThePast,
      showAsStory,
      placementPositions,
      purifiedFormik,
      isDomainValidating
    }),
    [isAdSetEndDateInThePast, showAsStory, placementPositions, purifiedFormik, isDomainValidating]
  )
}
